import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CalendarDotsIcon } from '@phosphor-icons/react';
import { Popover, PopoverContent } from '@voll-intelligence/ui-kit/ui/popover';
import { TravelCalendar, type DateRange } from '../../shared/travel_calendar.js';
import { TravelCalendarConfirm } from '../../shared/travel_calendar_confirm.js';
import { PageFieldSheet } from '../../shared/page_field_sheet.js';
import { returnFocusUnlessMoved } from '../../shared/return_focus.js';
import { SearchField } from '../../shared/search_field.js';
import { useGuidedOpen, type FieldGuide } from '../../shared/guided_flow.js';
import { RentalTimeSliders } from './rental_time_sliders.js';
import {
  DEFAULT_TIME,
  composeRentalWindow,
  dateOf,
  effectivePickupTime,
  isInvertedWindow,
  orderTimes,
  rentalDayCount,
  splitFloor,
  timeBounds,
  timeOf,
  type RentalTimes,
  type RentalWindow,
} from './rental_window.js';

/*
 * O campo do QUANDO da locação (#3446): retirada e devolução num período só, no
 * mesmo calendário do hotel — dois meses no popover, a folha de tela cheia com
 * a barra de meses e o Confirmar pregados no palco estreito. As HORAS moram no
 * mesmo painel, nas réguas em cima do calendário (#4513, o desenho do Figma):
 * o resumo do pé e o Confirmar falam da janela que a busca vai levar, com as
 * horas. No mesmo dia as réguas não deixam a janela inverter (`orderTimes`,
 * #5662), nos dois palcos: não há estado de erro.
 *
 * O rascunho (dias e horas) vive SÓ enquanto o painel está em cena (monta com
 * ele): abrir, mexer e desistir devolve a janela que a busca realmente tem. A abertura é
 * controlada e ancorada na pílula (`anchor`), sem `PopoverTrigger`: a pílula é
 * a `SearchField` compartilhada com o hotel, e ela já é um botão com o nome
 * acessível montado.
 */

export type RentalDatesCopy = {
  calendarLocale: string;
  previousMonthLabel: string;
  nextMonthLabel: string;
  /** A PERGUNTA da coluna e o título da folha ("Quando"). */
  datesLabel: string;
  /** A pílula sem período ("A definir"). */
  datesPlaceholder: string;
  /** A pílula com a janela, data E hora de cada ponta ("21 out, 10:00 – 24
   *  out, 18:30 · 4 dias"): as horas não têm mais campo próprio na fileira.
   *  `days` são as diárias da janela (`rentalDayCount`), não os dias do
   *  calendário. */
  windowSummary(
    window: { readonly pickupAt: string; readonly dropoffAt: string },
    days: number,
  ): string;
  /** O resumo do rascunho no pé do calendário, na régua do aéreo: "Seg, 21 Set
   *  → Qui, 24 Set · 3 dias"; com só a retirada, "Seg, 21 Set · escolha a
   *  devolução"; vazio, "Escolha a retirada e a devolução". `days` só vem com
   *  as duas pontas. */
  dateDraftSummary(range: DateRange, days: number | undefined): string;
  /** O Confirmar do popover — nomeia o período quando ele está fechado. */
  confirmDatesLabel(range: DateRange): string;
  /** O Confirmar FIXO da folha estreita ("Confirmar datas"): lá o período já
   *  está escrito no resumo logo acima. */
  confirmDatesSheetLabel: string;
  /** O Confirmar quando ele também BUSCA ("Confirmar e buscar"), nos dois
   *  palcos: o período já está no resumo logo acima. */
  confirmAndSearchLabel: string;
  /** As duas réguas de hora do painel ("Hora de retirada" / "Hora de devolução"). */
  pickupTimeLabel: string;
  dropoffTimeLabel: string;
};

type Layout = 'popover' | 'sheet';

type Draft = { readonly range: DateRange; readonly times: RentalTimes };

/** Os dias e as horas de uma janela; sem hora, a proposta do balcão. */
function draftOf(value: RentalWindow): Draft {
  return {
    range: { from: dateOf(value.pickupAt), to: dateOf(value.dropoffAt) },
    times: {
      pickup: timeOf(value.pickupAt) ?? DEFAULT_TIME,
      dropoff: timeOf(value.dropoffAt) ?? DEFAULT_TIME,
    },
  };
}

/** As diárias de uma janela inteira e em ordem; `undefined` senão. */
function daysOf(window: RentalWindow): number | undefined {
  return window.pickupAt && window.dropoffAt && !isInvertedWindow(window)
    ? rentalDayCount(window.pickupAt, window.dropoffAt)
    : undefined;
}

/** O calendário e as réguas com o RASCUNHO da janela — nasce a cada abertura
 *  a partir do valor confirmado. Mesmo dia é período válido no carro
 *  (`minNights: 0`): o rascunho guarda as horas sempre em ordem (`orderTimes`). */
function WindowDraft({
  initial,
  copy,
  pickupFloor,
  layout,
  searches,
  onConfirm,
}: {
  readonly initial: RentalWindow;
  readonly copy: RentalDatesCopy;
  readonly pickupFloor: string;
  readonly layout: Layout;
  readonly searches: boolean;
  readonly onConfirm: (window: RentalWindow) => void;
}): ReactNode {
  const [draft, setDraft] = useState<Draft>(() => {
    const opened = draftOf(initial);
    return { ...opened, times: orderTimes(opened.range, opened.times, pickupFloor) };
  });
  const update = (range: DateRange, times: RentalTimes) => {
    setDraft({ range, times: orderTimes(range, times, pickupFloor) });
  };
  const floor = splitFloor(pickupFloor);
  const window = composeRentalWindow(draft.range, draft.times, pickupFloor);
  const bounds = timeBounds(draft.range, draft.times.pickup, pickupFloor);
  const sheetLabel = searches ? copy.confirmAndSearchLabel : copy.confirmDatesSheetLabel;
  const sliders = (
    <RentalTimeSliders
      // A régua mostra a hora que vale: no dia do piso, nunca antes dele.
      pickup={effectivePickupTime(draft.range.from, draft.times.pickup, pickupFloor)}
      dropoff={draft.times.dropoff}
      pickupMin={draft.range.from === floor.day ? floor.time : undefined}
      pickupMax={bounds.pickupMax}
      dropoffMin={bounds.dropoffMin}
      copy={copy}
      inFooter={layout === 'sheet'}
      onChange={(times) => {
        update(draft.range, times);
      }}
    />
  );
  const confirm = (
    <TravelCalendarConfirm
      summary={copy.dateDraftSummary(draft.range, daysOf(window))}
      confirmLabel={
        layout === 'sheet' || searches ? sheetLabel : copy.confirmDatesLabel(draft.range)
      }
      // A ordem é das réguas; o cinto fica para o piso de hoje depois das
      // 23:00, que não deixa período de um dia (ver `orderTimes`).
      disabled={!draft.range.from || !draft.range.to || isInvertedWindow(window)}
      full={layout === 'sheet'}
      onConfirm={() => {
        onConfirm(window);
      }}
    />
  );
  return (
    // Na folha, a casca continua a corrente `flex-1 min-h-0` até o rolo do
    // calendário, e as réguas descem para o rodapé pregado embaixo, junto do
    // Confirmar (`DateSheetBody`), ao alcance do polegar. No popover, ficam em
    // cima do calendário.
    <div className={layout === 'sheet' ? 'flex min-h-0 flex-1 flex-col' : 'grid gap-2'}>
      {layout === 'popover' && sliders}
      <TravelCalendar
        value={draft.range}
        locale={copy.calendarLocale}
        previousMonthLabel={copy.previousMonthLabel}
        nextMonthLabel={copy.nextMonthLabel}
        minNights={0}
        // Depois da última meia-hora de hoje, o piso já é amanhã.
        minDate={floor.day}
        months={2}
        layout={layout}
        onChange={(range) => {
          update(range, draft.times);
        }}
        footer={
          layout === 'sheet' ? (
            <>
              {sliders}
              {confirm}
            </>
          ) : (
            confirm
          )
        }
      />
    </div>
  );
}

function windowText(value: RentalWindow, copy: RentalDatesCopy): string {
  const days = daysOf(value);
  return value.pickupAt && value.dropoffAt && days !== undefined
    ? copy.windowSummary({ pickupAt: value.pickupAt, dropoffAt: value.dropoffAt }, days)
    : copy.datesPlaceholder;
}

export function RentalDatesField({
  value,
  copy,
  pickupFloor,
  disabled,
  sheet,
  guide,
  searches = false,
  onConfirm,
}: {
  /** A janela confirmada (`YYYY-MM-DDTHH:mm` nas duas pontas). */
  readonly value: RentalWindow;
  readonly copy: RentalDatesCopy;
  /** O primeiro instante escolhível para a retirada (`YYYY-MM-DDTHH:mm`). */
  readonly pickupFloor: string;
  readonly disabled: boolean;
  /** FOLHA de tela cheia em vez do popover (o palco estreito). Ausente ⇒
   *  popover. */
  readonly sheet?: { readonly backLabel: string } | undefined;
  /** Ver `useGuidedFlow`. Ausente ⇒ sem anel e sem abertura por conta do guia. */
  readonly guide?: FieldGuide;
  /** O Confirmar também busca (ver `GuidedFlow.confirmSearches`): o rótulo diz. */
  readonly searches?: boolean;
  readonly onConfirm: (window: RentalWindow) => void;
}): ReactNode {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useGuidedOpen(guide);
  /* `busy` do host FECHA o painel de vez, não só o esconde: um Confirmar
     clicável durante a busca mudaria o pedido pelas costas dela, e um painel
     que reabrisse sozinho ao fim dela seria um gesto que ninguém fez. */
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled, setOpen]);
  const trigger = (
    <SearchField
      triggerRef={triggerRef}
      icon={CalendarDotsIcon}
      label={copy.datesLabel}
      text={windowText(value, copy)}
      muted={!value.pickupAt}
      naked
      wanted={guide?.wanted === true}
      expanded={open}
      disabled={disabled}
      onOpen={() => {
        setOpen(!open);
      }}
    />
  );
  const calendar = (layout: Layout) => (
    <WindowDraft
      initial={value}
      copy={copy}
      pickupFloor={pickupFloor}
      layout={layout}
      searches={searches}
      onConfirm={(window) => {
        setOpen(false);
        // O foco volta à pílula ANTES de o host saber do período: no
        // preenchimento guiado é ele quem, com tudo respondido, o leva ao
        // Buscar — e a última palavra tem de ser dele.
        triggerRef.current?.focus();
        onConfirm(window);
      }}
    />
  );
  if (sheet) {
    return (
      <>
        {trigger}
        <PageFieldSheet
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          // À pílula — salvo se o foco já pousou noutro lugar (o Buscar, no
          // preenchimento guiado; a folha do campo seguinte).
          returnFocusTo={() => returnFocusUnlessMoved(triggerRef)()}
          title={copy.datesLabel}
          backLabel={sheet.backLabel}
        >
          {calendar('sheet')}
        </PageFieldSheet>
      </>
    );
  }
  return (
    <>
      {trigger}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverContent
          anchor={triggerRef}
          align="start"
          aria-label={copy.datesLabel}
          className="w-auto gap-2 rounded-xl p-2"
        >
          {calendar('popover')}
        </PopoverContent>
      </Popover>
    </>
  );
}
